// The Inbox's Calls and Texts and email as two tabs of one page (BL-132, Blueprint), each with its
// count (talk-tabs-model.ts). A tab opens its own place, so each keeps its address; the ringing call's
// pop and the counts stay App's, kept current by its stream wherever you are.
import type { PageTab } from '@flowology/ui-web/patterns';
import { canSee, PLACES, type NavProps } from '../Nav.tsx';
import { TALK_TABS, talkCount } from './talk-tabs-model.ts';

/** The same two tabs as PageHead's (a pattern page's head: Texts and email); none when only one opens. */
export function talkTabs(
  nav: NavProps,
  callbacks?: number | null,
): { label: string; items: PageTab[] } | undefined {
  const held = nav.permissions ?? [];
  const tabs = TALK_TABS.filter((t) => PLACES.some((p) => p.view === t.view && canSee(p, held)));
  if (tabs.length < 2) return undefined;
  const waiting = {
    ringing: nav.ringing,
    callbacks: callbacks ?? nav.waiting?.callbacks ?? 0,
    replies: nav.waiting?.messages ?? 0,
  };
  return {
    // the rail's group for these two; "Inbox" would name a second navigation as the rail's own
    label: 'Talk',
    items: tabs.map((t) => {
      const count = talkCount(t.view, waiting);
      return {
        label: t.label,
        href: `#/${t.view}`,
        isCurrent: nav.view === t.view,
        onPress: () => nav.view !== t.view && nav.onView(t.view),
        ...(count ? { count: count.n } : {}),
      };
    }),
  };
}

export function TalkTabs({
  nav,
  callbacks,
}: {
  nav: NavProps;
  /** The callback queue the screen has read (Calls'), else the Inbox's own count of it. */
  callbacks?: number | null;
}) {
  const held = nav.permissions ?? [];
  // only the places this person opens: one left is no choice, so no tabs
  const tabs = TALK_TABS.filter((t) => PLACES.some((p) => p.view === t.view && canSee(p, held)));
  if (tabs.length < 2) return null;
  const waiting = {
    ringing: nav.ringing,
    callbacks: callbacks ?? nav.waiting?.callbacks ?? 0,
    replies: nav.waiting?.messages ?? 0,
  };
  return (
    <div className="tabs calls-tabs" role="tablist" aria-label="Inbox">
      {tabs.map((t) => {
        const here = nav.view === t.view;
        const count = talkCount(t.view, waiting);
        return (
          <button
            key={t.view}
            type="button"
            role="tab"
            aria-selected={here}
            className={here ? 'on' : ''}
            onClick={() => !here && nav.onView(t.view)}
          >
            {t.label}
            {count && (
              <>
                <span className="count" aria-hidden="true">
                  {count.n}
                </span>
                <span className="bp-vh">, {count.words}</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
